import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
} from '@nextui-org/react';
import { formatMoneyUSD } from '../../utils/functions';
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import { PreSettlementCalculationCriteria } from 'common/src/api/presettlement-settings';

interface ProcessedMovements {
  [producer: string]: {
    amount: number;
    kilos: number;
  };
}

interface ShipmentsTableProps {
  data: {
    [variety: string]: {
      [caliber: string]: {
        value: number;
        kilos: number;
        settledKilos: number;
        pricedSettledKilos: number;
        realSettledKilos: number;
        realValue: number;
      };
    };
  };
  producerData: {
    [producer: string]: {
      [variety: string]: {
        [caliber: string]: {
          value: number;
          kilos: number;
          settledKilos: number;
          pricedSettledKilos: number;
          realSettledKilos: number;
          realValue: number;
        };
      };
    };
  };
  movements?: ProcessedMovements;
  calibers: string[];
  groupBy: PreSettlementCalculationCriteria;
  viewMode: 'variety' | 'producer';
  showAdvanceColumns?: boolean;
}

export const PreSettlementsTable = ({
  data,
  producerData,
  movements,
  calibers,
  groupBy,
  viewMode,
  showAdvanceColumns = false,
}: ShipmentsTableProps) => {
  const { t } = useTranslation('preSettlements');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showTotal, setShowTotal] = useState(false);
  const [sortDescriptor, setSortDescriptor] = useState<{
    column?: string;
    direction?: 'ascending' | 'descending';
  }>({});

  // console.log('producerData', producerData);

  const columns = useMemo(
    () => [
      {
        name: viewMode === 'variety' ? t('VARIEDAD') : t('PRODUCTOR'),
        uid: viewMode === 'variety' ? 'variety' : 'producer',
      },
      ...calibers.map((caliber) => ({
        name:
          caliber === 'average'
            ? showTotal
              ? t('LIQUIDACIÓN A LA FECHA')
              : t('PROMEDIO LIQUIDADO')
            : caliber,
        uid: caliber,
      })),
      ...(viewMode === 'producer' && showTotal && calibers.includes('average')
        ? [
            {
              name: t('% LIQUIDADO'),
              uid: 'settledPercentage',
            },
          ]
        : []),
      ...(viewMode === 'producer' && showTotal && calibers.includes('average')
        ? [
            {
              name: t('% REAL LIQUIDADO'),
              uid: 'realSettledPercentage',
            },
          ]
        : []),
      ...(viewMode === 'producer' && showTotal && calibers.includes('average')
        ? [
            {
              name: t('LIQUIDACIÓN PROYECTADA'),
              uid: 'projected',
            },
          ]
        : []),
      ...(viewMode === 'producer' && showTotal && calibers.includes('average')
        ? [
            {
              name: t('LIQUIDACIÓN REAL'),
              uid: 'realProjected',
            },
          ]
        : []),
      ...(showAdvanceColumns &&
      viewMode === 'producer' &&
      calibers.includes('average')
        ? [
            {
              name: showTotal
                ? t('ANTICIPO TOTAL')
                : t('ANTICIPO POR KG DESPACHADO'),
              uid: 'advanced',
            },
          ]
        : []),
      ...(showAdvanceColumns &&
      viewMode === 'producer' &&
      calibers.includes('average')
        ? [
            {
              name: t('SALDO A LA FECHA'),
              uid: 'balance',
            },
          ]
        : []),
      ...(viewMode === 'producer' && showTotal && calibers.includes('average')
        ? [
            {
              name: t('SALDO PROYECTADO'),
              uid: 'projectedBalance',
            },
          ]
        : []),
      ...(calibers.includes('average')
        ? [
            {
              name: t('KILOS DESPACHADOS'),
              uid: 'dispatchedKilos',
            },
          ]
        : []),
    ],
    [calibers, viewMode, showTotal, showAdvanceColumns, t]
  );

  const rows = useMemo(() => {
    // Calculate totals first
    const totalsRow = {
      id: 'totals',
      variety: 'TOTAL',
      producer: 'TOTAL',
      isMainRow: true,
      ...calibers.reduce((acc, caliber) => {
        if (viewMode === 'variety') {
          // console.log('data', data);
          // Calculate totals across all varieties
          let totalValue = 0;
          let totalKilos = 0;
          let totalSettledKilos = 0;
          let totalPricedSettledKilos = 0;
          let totalRealValue = 0;
          let totalRealSettledKilos = 0;

          if (showTotal) {
            // When showing total USD, sum the total values
            totalValue = Object.values(data).reduce((sum, varietyData) => {
              const caliberInfo = varietyData[caliber] || {
                value: 0,
                kilos: 0,
                settledKilos: 0,
                pricedSettledKilos: 0,
                realSettledKilos: 0,
                realValue: 0,
              };
              return (
                sum +
                (caliberInfo.value || 0) *
                  (caliberInfo.pricedSettledKilos || 0)
              );
            }, 0);
            totalRealValue = Object.values(data).reduce((sum, varietyData) => {
              const caliberInfo = varietyData[caliber] || {
                value: 0,
                kilos: 0,
                settledKilos: 0,
                realSettledKilos: 0,
                realValue: 0,
              };
              return (
                sum +
                (caliberInfo.realValue || 0) *
                  (caliberInfo.realSettledKilos || 0)
              );
            }, 0);
          } else {
            // When showing USD per kg, calculate weighted average

            let totalWeightedValue = 0;
            let totalRealWeightedValue = 0;
            Object.values(data).forEach((varietyData) => {
              const caliberInfo = varietyData[caliber] || {
                value: 0,
                kilos: 0,
                settledKilos: 0,
                pricedSettledKilos: 0,
                realSettledKilos: 0,
                realValue: 0,
              };
              totalWeightedValue +=
                (caliberInfo.value || 0) *
                (caliberInfo.pricedSettledKilos || 0);
              totalSettledKilos += caliberInfo.settledKilos || 0;
              totalPricedSettledKilos += caliberInfo.pricedSettledKilos || 0;
              totalRealWeightedValue +=
                (caliberInfo.realValue || 0) *
                (caliberInfo.realSettledKilos || 0);
              totalRealSettledKilos += caliberInfo.realSettledKilos || 0;
            });
            totalValue =
              totalPricedSettledKilos > 0
                ? totalWeightedValue / totalPricedSettledKilos
                : 0;
            totalRealValue =
              totalRealSettledKilos > 0
                ? totalRealWeightedValue / totalRealSettledKilos
                : 0;
          }

          // Calculate total settled percentage
          totalKilos = Object.values(data).reduce((sum, varietyData) => {
            const caliberInfo = varietyData[caliber] || {
              value: 0,
              kilos: 0,
              settledKilos: 0,
              pricedSettledKilos: 0,
              realSettledKilos: 0,
              realValue: 0,
            };
            return sum + caliberInfo.kilos;
          }, 0);

          if (showTotal) {
            totalSettledKilos = Object.values(data).reduce(
              (sum, varietyData) => {
                const caliberInfo = varietyData[caliber] || {
                  value: 0,
                  kilos: 0,
                  settledKilos: 0,
                  pricedSettledKilos: 0,
                  realSettledKilos: 0,
                  realValue: 0,
                };
                return sum + caliberInfo.settledKilos;
              },
              0
            );
            totalRealSettledKilos = Object.values(data).reduce(
              (sum, varietyData) => {
                const caliberInfo = varietyData[caliber] || {
                  value: 0,
                  kilos: 0,
                  settledKilos: 0,
                  pricedSettledKilos: 0,
                  realSettledKilos: 0,
                  realValue: 0,
                };
                return sum + caliberInfo.realSettledKilos;
              },
              0
            );
          }

          const settledPercentage =
            totalKilos > 0 ? (totalSettledKilos / totalKilos) * 100 : 0;
          const realSettledPercentage =
            totalKilos > 0 ? (totalRealSettledKilos / totalKilos) * 100 : 0;

          return {
            ...acc,
            [caliber]: totalValue,
            [`${caliber}_settled`]: settledPercentage,
            [`${caliber}_realSettled`]: realSettledPercentage,
            [`${caliber}_realValue`]: totalRealValue,
          };
        } else {
          // Sum up totals across all producers
          let totalValue = 0;
          let totalKilos = 0;
          let totalSettledKilos = 0;
          let totalPricedSettledKilos = 0;
          let totalRealValue = 0;
          let totalRealSettledKilos = 0;

          Object.values(producerData).forEach((varieties) => {
            Object.values(varieties).forEach((varietyData) => {
              const caliberData = varietyData[caliber];
              if (caliberData) {
                totalValue +=
                  caliberData.value * caliberData.pricedSettledKilos;
                totalKilos += caliberData.kilos;
                totalSettledKilos += caliberData.settledKilos;
                totalPricedSettledKilos += caliberData.pricedSettledKilos;
                totalRealValue +=
                  (caliberData.realValue || 0) *
                  (caliberData.realSettledKilos || 0);
                totalRealSettledKilos += caliberData.realSettledKilos || 0;
              }
            });
          });

          const settledPercentage =
            totalKilos > 0 ? (totalSettledKilos / totalKilos) * 100 : 0;
          const realSettledPercentage =
            totalKilos > 0 ? (totalRealSettledKilos / totalKilos) * 100 : 0;

          return {
            ...acc,
            [caliber]: showTotal
              ? totalValue
              : totalPricedSettledKilos > 0
                ? totalValue / totalPricedSettledKilos
                : 0,
            [`${caliber}_settled`]: settledPercentage,
            [`${caliber}_realSettled`]: realSettledPercentage,
            [`${caliber}_realValue`]: showTotal
              ? totalRealValue
              : totalRealSettledKilos > 0
                ? totalRealValue / totalRealSettledKilos
                : 0,
          };
        }
      }, {}),
    };

    // console.log('totalsRow', totalsRow);

    // Add projected calculation for totals row
    if (viewMode === 'producer') {
      let totalProjectedValue = 0;

      Object.values(producerData).forEach((varieties) => {
        Object.values(varieties).forEach((varietyData) => {
          const averageData = varietyData['average'] || { value: 0, kilos: 0 };
          totalProjectedValue += averageData.value * averageData.kilos;
        });
      });

      totalsRow.projected = totalProjectedValue;

      // Modified to only sum advances for producers that exist in producerData
      const totalAdvanced = Object.entries(movements || {})
        .filter(([producer]) => producerData.hasOwnProperty(producer))
        .reduce((sum, [_, movement]) => sum + (movement.amount || 0), 0);

      const totalKilos = Object.values(producerData).reduce(
        (sum, varieties) =>
          sum +
          Object.values(varieties).reduce(
            (varietySum, varietyData) =>
              varietySum +
              Object.values(varietyData).reduce(
                (caliberSum, caliberData) =>
                  caliberSum + (caliberData.kilos || 0),
                0
              ),
            0
          ),
        0
      );

      totalsRow.advanced = showTotal
        ? totalAdvanced
        : totalKilos > 0
          ? totalAdvanced / totalKilos
          : 0;

      const totalValue = showTotal
        ? calibers.reduce((sum, caliber) => sum + (totalsRow[caliber] || 0), 0)
        : totalsRow.average;

      totalsRow.balance = showTotal
        ? totalValue - totalAdvanced
        : totalValue - (totalKilos > 0 ? totalAdvanced / totalKilos : 0);

      // Add projected balance calculation for totals row
      totalsRow.projectedBalance = totalProjectedValue - totalAdvanced;
    }

    // Calculate dispatched kilos for totals row (both view modes)
    {
      let totalDispatchedKilos = 0;
      if (viewMode === 'variety') {
        Object.values(data).forEach((varietyData) => {
          Object.values(varietyData).forEach((caliberData) => {
            totalDispatchedKilos += caliberData.kilos || 0;
          });
        });
      } else {
        Object.values(producerData).forEach((varieties) => {
          Object.values(varieties).forEach((varietyData) => {
            Object.values(varietyData).forEach((caliberData) => {
              totalDispatchedKilos += caliberData.kilos || 0;
            });
          });
        });
      }
      totalsRow.dispatchedKilos = totalDispatchedKilos;
    }

    // Get existing rows calculation
    const existingRows =
      viewMode === 'variety'
        ? Object.entries(data).map(([variety, caliberData]) => ({
            id: `variety-${variety}`,
            variety,
            isMainRow: true,
            dispatchedKilos: Object.values(caliberData).reduce(
              (sum, info) => sum + (info.kilos || 0),
              0
            ),
            ...calibers.reduce((acc, caliber) => {
              const caliberInfo = caliberData[caliber] || {
                value: 0,
                kilos: 0,
                settledKilos: 0,
                pricedSettledKilos: 0,
                realSettledKilos: 0,
                realValue: 0,
              };
              const settledPercentage =
                caliberInfo.kilos > 0
                  ? (caliberInfo.settledKilos / caliberInfo.kilos) * 100
                  : 0;
              const realSettledPercentage =
                caliberInfo.kilos > 0
                  ? (caliberInfo.realSettledKilos / caliberInfo.kilos) * 100
                  : 0;

              return {
                ...acc,
                [caliber]: showTotal
                  ? (caliberInfo.value || 0) *
                    (caliberInfo.pricedSettledKilos || 0)
                  : caliberInfo.value || 0,
                [`${caliber}_settled`]: settledPercentage,
                [`${caliber}_realSettled`]: realSettledPercentage,
                [`${caliber}_realValue`]: showTotal
                  ? (caliberInfo.realValue || 0) *
                    (caliberInfo.realSettledKilos || 0)
                  : caliberInfo.realValue || 0,
              };
            }, {}),
          }))
        : Object.entries(producerData).flatMap(([producer, varieties]) => {
            // Calculate producer totals
            const producerTotal = calibers.reduce((acc, caliber) => {
              let totalValue = 0;
              let totalKilos = 0;
              let totalSettledKilos = 0;
              let totalPricedSettledKilos = 0;
              let totalRealValue = 0;
              let totalRealSettledKilos = 0;

              Object.values(varieties).forEach((varietyData) => {
                const caliberData = varietyData[caliber];
                if (caliberData) {
                  totalValue +=
                    caliberData.value * caliberData.pricedSettledKilos;
                  totalKilos += caliberData.kilos;
                  totalSettledKilos += caliberData.settledKilos;
                  totalPricedSettledKilos += caliberData.pricedSettledKilos;
                  totalRealValue +=
                    (caliberData.realValue || 0) *
                    (caliberData.realSettledKilos || 0);
                  totalRealSettledKilos += caliberData.realSettledKilos || 0;
                }
              });

              const settledPercentage =
                totalKilos > 0 ? (totalSettledKilos / totalKilos) * 100 : 0;
              const realSettledPercentage =
                totalKilos > 0 ? (totalRealSettledKilos / totalKilos) * 100 : 0;

              return {
                ...acc,
                [caliber]: showTotal
                  ? totalValue
                  : totalPricedSettledKilos > 0
                    ? totalValue / totalPricedSettledKilos
                    : 0,
                [`${caliber}_settled`]: settledPercentage,
                [`${caliber}_realSettled`]: realSettledPercentage,
                [`${caliber}_realValue`]: showTotal
                  ? totalRealValue
                  : totalRealSettledKilos > 0
                    ? totalRealValue / totalRealSettledKilos
                    : 0,
              };
            }, {});

            const producerKilos = Object.values(varieties).reduce(
              (sum, varietyData) =>
                sum +
                Object.values(varietyData).reduce(
                  (caliberSum, caliberData) =>
                    caliberSum + (caliberData.kilos || 0),
                  0
                ),
              0
            );

            const mainRow = {
              id: `producer-${producer}`,
              producer,
              variety: producer,
              isMainRow: true,
              isExpanded: expandedRows.has(producer),
              dispatchedKilos: producerKilos,
              ...producerTotal,
            };

            const varietyRows = Object.entries(varieties).map(
              ([variety, caliberData]) => ({
                id: `producer-${producer}-variety-${variety}`,
                producer,
                variety,
                isMainRow: false,
                dispatchedKilos: Object.values(caliberData).reduce(
                  (sum, info) => sum + (info.kilos || 0),
                  0
                ),
                ...calibers.reduce((acc, caliber) => {
                  const info = caliberData[caliber] || {
                    value: 0,
                    kilos: 0,
                    settledKilos: 0,
                    pricedSettledKilos: 0,
                  };
                  const settledPercentage =
                    info.kilos > 0
                      ? (info.settledKilos / info.kilos) * 100
                      : 0;
                  const realSettledPercentage =
                    info.kilos > 0
                      ? (info.realSettledKilos / info.kilos) * 100
                      : 0;

                  return {
                    ...acc,
                    [caliber]: showTotal
                      ? (info.value || 0) * (info.pricedSettledKilos || 0)
                      : info.value || 0,
                    [`${caliber}_settled`]: settledPercentage,
                    [`${caliber}_realSettled`]: realSettledPercentage,
                  };
                }, {}),
              })
            );

            return [
              mainRow,
              ...(expandedRows.has(producer) ? varietyRows : []),
            ];
          });

    // Return combined array with totals row first
    if (process.env.NODE_ENV !== 'production') {
      const totalsDebug = calibers.reduce((acc, caliber) => {
        let totalKilos = 0;
        let totalSettledKilos = 0;
        let totalPricedSettledKilos = 0;

        if (viewMode === 'variety') {
          Object.values(data).forEach((varietyData) => {
            const info = varietyData[caliber];
            if (!info) return;
            totalKilos += info.kilos || 0;
            totalSettledKilos += info.settledKilos || 0;
            totalPricedSettledKilos += info.pricedSettledKilos || 0;
          });
        } else {
          Object.values(producerData).forEach((varieties) => {
            Object.values(varieties).forEach((varietyData) => {
              const info = varietyData[caliber];
              if (!info) return;
              totalKilos += info.kilos || 0;
              totalSettledKilos += info.settledKilos || 0;
              totalPricedSettledKilos += info.pricedSettledKilos || 0;
            });
          });
        }

        acc[caliber] = {
          totalKilos,
          totalSettledKilos,
          totalPricedSettledKilos,
        };
        return acc;
      }, {} as Record<string, { totalKilos: number; totalSettledKilos: number; totalPricedSettledKilos: number }>);

      const producersDebug = Object.entries(producerData).map(
        ([producer, varieties]) => {
          const calibersInfo = calibers.reduce((acc, caliber) => {
            let totalKilos = 0;
            let totalSettledKilos = 0;
            let totalPricedSettledKilos = 0;
            Object.values(varieties).forEach((varietyData) => {
              const info = varietyData[caliber];
              if (!info) return;
              totalKilos += info.kilos || 0;
              totalSettledKilos += info.settledKilos || 0;
              totalPricedSettledKilos += info.pricedSettledKilos || 0;
            });
            acc[caliber] = {
              totalKilos,
              totalSettledKilos,
              totalPricedSettledKilos,
            };
            return acc;
          }, {} as Record<string, { totalKilos: number; totalSettledKilos: number; totalPricedSettledKilos: number }>);

          const varietiesInfo = Object.entries(varieties).map(
            ([variety, caliberData]) => ({
              variety,
              calibers: calibers.reduce((acc, caliber) => {
                const info = caliberData[caliber] || {
                  kilos: 0,
                  settledKilos: 0,
                  pricedSettledKilos: 0,
                };
                acc[caliber] = {
                  totalKilos: info.kilos || 0,
                  totalSettledKilos: info.settledKilos || 0,
                  totalPricedSettledKilos: info.pricedSettledKilos || 0,
                };
                return acc;
              }, {} as Record<string, { totalKilos: number; totalSettledKilos: number; totalPricedSettledKilos: number }>),
            })
          );

          return { producer, calibers: calibersInfo, varieties: varietiesInfo };
        }
      );

      console.log('PreSettlementsTable kilos debug', {
        viewMode,
        totals: totalsDebug,
        producers: producersDebug,
      });
    }

    return [totalsRow, ...existingRows];
  }, [
    data,
    producerData,
    calibers,
    viewMode,
    expandedRows,
    showTotal,
    movements,
  ]);

  const getSortValue = useCallback(
    (row: any, columnKey: string): string | number => {
      if (columnKey === 'variety' || columnKey === 'producer') {
        return row.variety || row.producer || '';
      }
      if (columnKey === 'dispatchedKilos') {
        return row.dispatchedKilos || 0;
      }
      if (columnKey === 'settledPercentage') {
        return row['average_settled'] || 0;
      }
      if (columnKey === 'realSettledPercentage') {
        return row['average_realSettled'] || 0;
      }
      if (columnKey === 'projected') {
        if (!row.isMainRow) {
          const varietyData = producerData[row.producer]?.[row.variety] || {};
          const avg = varietyData['average'] || { value: 0, kilos: 0 };
          return avg.value * avg.kilos;
        }
        const producerVarieties = producerData[row.producer] || {};
        let total = 0;
        Object.values(producerVarieties).forEach((vd: any) => {
          const avg = vd['average'] || { value: 0, kilos: 0 };
          total += avg.value * avg.kilos;
        });
        return total;
      }
      if (columnKey === 'realProjected') {
        if (!row.isMainRow) {
          const varietyData = producerData[row.producer]?.[row.variety] || {};
          const avg = varietyData['average'] || {
            realValue: 0,
            realSettledKilos: 0,
          };
          return (avg.realValue || 0) * (avg.realSettledKilos || 0);
        }
        return Object.values(producerData[row.producer] || {}).reduce(
          (sum: number, vd: any) => {
            const avg = vd['average'] || {
              realValue: 0,
              realSettledKilos: 0,
            };
            return sum + (avg.realValue || 0) * (avg.realSettledKilos || 0);
          },
          0
        );
      }
      if (columnKey === 'advanced') {
        if (!row.isMainRow) return 0;
        const advancedAmount = movements?.[row.producer]?.amount || 0;
        if (showTotal) return advancedAmount;
        const totalKilos = Object.values(
          producerData[row.producer] || {}
        ).reduce(
          (sum: number, vd: any) =>
            sum +
            Object.values(vd).reduce(
              (cs: number, cd: any) => cs + (cd.kilos || 0),
              0
            ),
          0
        );
        return totalKilos > 0 ? advancedAmount / totalKilos : 0;
      }
      if (columnKey === 'balance') {
        if (!row.isMainRow) return 0;
        const advancedAmount = movements?.[row.producer]?.amount || 0;
        const totalKilos = Object.values(
          producerData[row.producer] || {}
        ).reduce(
          (sum: number, vd: any) =>
            sum +
            Object.values(vd).reduce(
              (cs: number, cd: any) => cs + (cd.kilos || 0),
              0
            ),
          0
        );
        const value = showTotal
          ? calibers.reduce((sum, cal) => sum + (row[cal] || 0), 0)
          : row.average;
        const advancePerKg = totalKilos > 0 ? advancedAmount / totalKilos : 0;
        return showTotal ? value - advancedAmount : value - advancePerKg;
      }
      if (columnKey === 'projectedBalance') {
        if (!row.isMainRow) return 0;
        const advancedAmount = movements?.[row.producer]?.amount || 0;
        let totalProjected = 0;
        Object.values(producerData[row.producer] || {}).forEach((vd: any) => {
          const avg = vd['average'] || { value: 0, kilos: 0 };
          totalProjected += avg.value * avg.kilos;
        });
        return totalProjected - advancedAmount;
      }
      // Default: caliber columns
      return Number(row[columnKey]) || 0;
    },
    [producerData, movements, showTotal, calibers]
  );

  const sortedRows = useMemo(() => {
    if (!sortDescriptor.column) return rows;

    const columnKey = sortDescriptor.column as string;
    const direction = sortDescriptor.direction;

    const [totalsRow, ...dataRows] = rows;

    const compareValues = (a: any, b: any): number => {
      const aVal = getSortValue(a, columnKey);
      const bVal = getSortValue(b, columnKey);
      let result: number;
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        result = aVal.localeCompare(bVal);
      } else {
        result = (aVal as number) - (bVal as number);
      }
      return direction === 'descending' ? -result : result;
    };

    if (viewMode === 'variety') {
      const sorted = [...dataRows].sort(compareValues);
      return [totalsRow, ...sorted];
    }

    // Producer view: group main rows with their sub-rows
    const groups: { main: any; subRows: any[] }[] = [];
    let currentGroup: { main: any; subRows: any[] } | null = null;

    dataRows.forEach((row) => {
      if (row.isMainRow) {
        currentGroup = { main: row, subRows: [] };
        groups.push(currentGroup);
      } else if (currentGroup) {
        currentGroup.subRows.push(row);
      }
    });

    groups.sort((a, b) => compareValues(a.main, b.main));
    groups.forEach((group) => {
      group.subRows.sort(compareValues);
    });

    const sorted = groups.flatMap((g) => [g.main, ...g.subRows]);
    return [totalsRow, ...sorted];
  }, [rows, sortDescriptor, getSortValue, viewMode]);

  const renderCell = (row: any, columnKey: string) => {
    // Add special styling for totals row
    if (row.id === 'totals') {
      const value = row[columnKey];
      return (
        <div className='font-bold text-sm'>
          {columnKey === 'variety' || columnKey === 'producer' ? (
            'TOTAL'
          ) : columnKey === 'dispatchedKilos' ? (
            value ? Math.round(value).toLocaleString() : '-'
          ) : columnKey === 'settledPercentage' ? (
            `${(row['average_settled'] || 0).toFixed(2)}%`
          ) : typeof value === 'number' ? (
            <>
              <span
                className={`${columnKey === 'projectedBalance' ? (value < 0 ? 'text-red-500' : 'text-green-500') : ''}`}
              >
                {formatMoneyUSD(value, { decimals: 2 })}
              </span>
              {row[`${columnKey}_settled`] !== undefined &&
                !(
                  viewMode === 'producer' &&
                  showTotal &&
                  calibers.includes('average')
                ) && (
                  <span className='text-xs text-default-400 ml-1'>
                    ({(row[`${columnKey}_settled`] || 0).toFixed(2)}%{' '}
                    {t('liquidado')})
                  </span>
                )}
            </>
          ) : (
            value
          )}
        </div>
      );
    }

    if (columnKey === 'variety' || columnKey === 'producer') {
      return (
        <div className='flex items-center gap-2'>
          {viewMode === 'producer' && row.isMainRow && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setExpandedRows((prev) => {
                  const next = new Set(prev);
                  if (row.isExpanded) {
                    next.delete(row.producer);
                  } else {
                    next.add(row.producer);
                  }
                  return next;
                });
              }}
              className='p-1'
            >
              {row.isExpanded ? '−' : '+'}
            </button>
          )}
          <p className={`text-sm ${row.isMainRow ? 'font-medium' : 'pl-8'}`}>
            {row.variety}
          </p>
        </div>
      );
    }

    if (columnKey === 'projected') {
      // For main rows (producers), calculate total across all their varieties
      if (row.isMainRow) {
        const producerVarieties = producerData[row.producer] || {};
        let totalProjectedValue = 0;

        Object.values(producerVarieties).forEach((varietyData) => {
          Object.entries(varietyData).forEach(([caliber, data]) => {
            if (caliber === 'average') {
              totalProjectedValue += data.value * data.kilos;
            }
          });
        });

        return (
          <div className='text-sm text-blue-500'>
            {totalProjectedValue === 0
              ? '-'
              : formatMoneyUSD(totalProjectedValue, { decimals: 2 })}
          </div>
        );
      }

      // For sub-rows (varieties), calculate just that variety's projection
      const varietyData = producerData[row.producer]?.[row.variety] || {};
      const averageData = varietyData['average'] || { value: 0, kilos: 0 };
      const projectedValue = averageData.value * averageData.kilos;

      return (
        <div className='text-sm text-blue-500'>
          {projectedValue === 0
            ? '-'
            : formatMoneyUSD(projectedValue, { decimals: 2 })}
        </div>
      );
    }

    if (columnKey === 'settledPercentage') {
      return (
        <div className='text-sm'>
          {(row['average_settled'] || 0).toFixed(2)}%
        </div>
      );
    }

    if (columnKey === 'realSettledPercentage') {
      return (
        <div className='text-sm'>
          {Math.round(row['average_realSettled'] || 0)}%
        </div>
      );
    }

    if (columnKey === 'realProjected') {
      if (!row.isMainRow) {
        // For sub-rows (varieties), calculate just that variety's real projection
        const varietyData = producerData[row.producer]?.[row.variety] || {};
        const averageData = varietyData['average'] || {
          realValue: 0,
          realSettledKilos: 0,
        };
        const realProjectedValue =
          (averageData.realValue || 0) * (averageData.realSettledKilos || 0);

        return (
          <div className='text-sm text-green-500'>
            {realProjectedValue === 0
              ? '-'
              : formatMoneyUSD(realProjectedValue, { decimals: 2 })}
          </div>
        );
      }

      // For main rows (producers), sum all varieties' real projections
      const producerData_copy = producerData[row.producer] || {};
      const totalRealProjectedValue = Object.values(producerData_copy).reduce(
        (sum, varietyData) => {
          const averageData = varietyData['average'] || {
            realValue: 0,
            realSettledKilos: 0,
          };
          return (
            sum +
            (averageData.realValue || 0) * (averageData.realSettledKilos || 0)
          );
        },
        0
      );

      return (
        <div className='text-sm text-green-600 dark:text-green-400 font-medium'>
          {totalRealProjectedValue === 0
            ? '-'
            : formatMoneyUSD(totalRealProjectedValue, { decimals: 2 })}
        </div>
      );
    }

    if (columnKey === 'dispatchedKilos') {
      const value = row.dispatchedKilos;
      if (!value || value === 0)
        return <p className='text-sm text-default-300'>-</p>;
      return (
        <div className='text-sm'>
          {Math.round(value).toLocaleString()}
        </div>
      );
    }

    if (columnKey === 'advanced') {
      if (!row.isMainRow) return null;
      const advancedAmount = movements?.[row.producer]?.amount || 0;
      // Get total kilos from the main row's caliber data
      const totalKilos = Object.values(producerData[row.producer] || {}).reduce(
        (sum, varietyData) => {
          return (
            sum +
            Object.values(varietyData).reduce((caliberSum, caliberData) => {
              return caliberSum + (caliberData.kilos || 0);
            }, 0)
          );
        },
        0
      );

      return (
        <div className='text-sm'>
          {formatMoneyUSD(
            showTotal
              ? advancedAmount
              : totalKilos > 0
                ? advancedAmount / totalKilos
                : 0,
            { decimals: 2 }
          )}
        </div>
      );
    }

    if (columnKey === 'balance') {
      if (!row.isMainRow) return null;
      const advancedAmount = movements?.[row.producer]?.amount || 0;
      const totalKilos = Object.values(producerData[row.producer] || {}).reduce(
        (sum, varietyData) => {
          return (
            sum +
            Object.values(varietyData).reduce((caliberSum, caliberData) => {
              return caliberSum + (caliberData.kilos || 0);
            }, 0)
          );
        },
        0
      );

      const value = showTotal
        ? calibers.reduce((sum, caliber) => sum + (row[caliber] || 0), 0)
        : row.average;

      const advancePerKg = totalKilos > 0 ? advancedAmount / totalKilos : 0;

      const balance = showTotal ? value - advancedAmount : value - advancePerKg;

      return (
        <div
          className={`text-sm ${balance < 0 ? 'text-red-500' : 'text-green-500'}`}
        >
          {formatMoneyUSD(balance, { decimals: 2 })}
        </div>
      );
    }

    if (columnKey === 'projectedBalance') {
      if (!row.isMainRow) return null;
      const advancedAmount = movements?.[row.producer]?.amount || 0;

      // For totals row
      if (row.id === 'totals') {
        const projectedBalance = row.projected - advancedAmount;
        return (
          <div
            className={`text-sm font-bold ${projectedBalance < 0 ? 'text-red-500' : 'text-green-500'}`}
          >
            {formatMoneyUSD(projectedBalance, { decimals: 2 })}
          </div>
        );
      }

      // For producer rows
      const producerVarieties = producerData[row.producer] || {};
      let totalProjectedValue = 0;

      Object.values(producerVarieties).forEach((varietyData) => {
        const averageData = varietyData['average'] || { value: 0, kilos: 0 };
        totalProjectedValue += averageData.value * averageData.kilos;
      });

      const projectedBalance = totalProjectedValue - advancedAmount;

      return (
        <div
          className={`text-sm ${projectedBalance < 0 ? 'text-red-500' : 'text-green-500'}`}
        >
          {formatMoneyUSD(projectedBalance, { decimals: 2 })}
        </div>
      );
    }

    const value = Number(row[columnKey]);
    const settledPercentage = row[`${columnKey}_settled`];

    if (typeof value === 'number') {
      if (value === 0) {
        return <p className='text-sm text-default-300'>-</p>;
      }
      return (
        <div className='text-sm'>
          <span>
            {isNaN(value) ? '-' : formatMoneyUSD(value, { decimals: 2 })}
          </span>
          {settledPercentage !== undefined &&
            !(
              viewMode === 'producer' &&
              showTotal &&
              calibers.includes('average')
            ) && (
              <span className='text-xs text-default-400 ml-1'>
                ({(settledPercentage || 0).toFixed(2)}% {t('liquidados')})
              </span>
            )}
        </div>
      );
    }
    return <p className='text-sm'>{value}</p>;
  };

  return (
    <div className='overflow-x-auto'>
      <div className='mb-4 mx-2 flex justify-between items-center gap-3'>
        <h6 className='text-lg font-bold'>{t('Resultados')}</h6>
        {/* <div className='flex items-center gap-3'>
        <span className="text-sm text-default-600 font-medium">USD por Kilo</span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={showTotal}
            onChange={() => setShowTotal(prev => !prev)}
          />
          <div className="w-14 h-7 bg-default-200 rounded-full peer peer-checked:after:translate-x-7 rtl:peer-checked:after:-translate-x-7 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-content1 after:border-default-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all dark:border-default-600 peer-checked:bg-blue-500"></div>
        </label>
        <span className="text-sm text-default-600 font-medium">USD Total</span></div> */}
      </div>
      <Table
        removeWrapper
        aria-label={t('Tabla de valores por criterio')}
        className='min-w-full'
        sortDescriptor={sortDescriptor as any}
        onSortChange={(descriptor) => setSortDescriptor(descriptor as any)}
      >
        <TableHeader columns={columns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              className={column.uid === 'variety' ? 'bg-default-100' : ''}
              allowsSorting
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody items={sortedRows}>
          {(row) => (
            <TableRow key={row.id}>
              {(columnKey) => (
                <TableCell>{renderCell(row, columnKey)}</TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
